/**
 * ReviewStep Component
 *
 * Step 3 of the vendor registration wizard.
 * Shows a summary of all entered data and terms acceptance checkbox.
 */

import { useState, useEffect } from 'react';
import { Stack, Group } from '@/components/ui/layout';
import { Text, Button, Anchor } from '@/components/ui/core';
import { Card, Divider } from '@/components/ui/data-display';
import { Checkbox } from '@/components/ui/inputs';
import { Alert } from '@/components/ui/feedback';
import { IconUser, IconBuilding, IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useVendorsServices } from '@actschurch/ccms-sdk/react';
import type { AccountInfoData } from './AccountInfoStep';
import type { BusinessInfoData } from './BusinessInfoStep';

interface ReviewStepProps {
  accountData: AccountInfoData;
  businessData: BusinessInfoData;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  submitError: string | null;
  /** Whether the user is already authenticated (hides account info section) */
  isAuthenticated?: boolean;
}

export function ReviewStep({
  accountData,
  businessData,
  onBack,
  onSubmit,
  isSubmitting,
  submitError,
  isAuthenticated = false,
}: ReviewStepProps) {
  const vendorServices = useVendorsServices();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState('');
  const [vendorTypeName, setVendorTypeName] = useState('');

  // Load vendor type name
  useEffect(() => {
    const loadVendorType = async () => {
      if (businessData.vendorTypeID) {
        try {
          const type = await vendorServices.vendorType.getById(businessData.vendorTypeID);
          setVendorTypeName(type?.name || 'Unknown');
        } catch {
          setVendorTypeName('Unknown');
        }
      }
    };
    loadVendorType();
  }, [businessData.vendorTypeID, vendorServices]);

  const handleSubmit = () => {
    if (!termsAccepted) {
      setTermsError('You must accept the terms and conditions');
      return;
    }
    setTermsError('');
    onSubmit();
  };

  return (
    <Stack gap="md">
      {/* Error Alert */}
      {submitError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Registration Failed"
          color="red"
        >
          {submitError}
        </Alert>
      )}

      {/* Account Information Summary - only shown for anonymous registration */}
      {!isAuthenticated && (
        <Card withBorder p="md" radius="md">
          <Stack gap="sm">
            <Group gap="xs">
              <IconUser size={20} color="var(--mantine-color-indigo-6)" />
              <Text fw={600} size="md">Account Information</Text>
            </Group>
            <Divider />
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Name</Text>
              <Text size="sm" fw={500}>{accountData.userFirstName} {accountData.userSurname}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Email</Text>
              <Text size="sm" fw={500}>{accountData.userEmail}</Text>
            </Group>
            {accountData.userCellphone && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Cellphone</Text>
                <Text size="sm" fw={500}>{accountData.userCellphone}</Text>
              </Group>
            )}
          </Stack>
        </Card>
      )}

      {/* Business Information Summary */}
      <Card withBorder p="md" radius="md">
        <Stack gap="sm">
          <Group gap="xs">
            <IconBuilding size={20} color="var(--mantine-color-indigo-6)" />
            <Text fw={600} size="md">Business Information</Text>
          </Group>
          <Divider />
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Business Name</Text>
            <Text size="sm" fw={500}>{businessData.businessName}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Vendor Type</Text>
            <Text size="sm" fw={500}>{vendorTypeName || 'Loading...'}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Phone</Text>
            <Text size="sm" fw={500}>{businessData.phoneNumber}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">Email</Text>
            <Text size="sm" fw={500}>{businessData.email}</Text>
          </Group>
          {businessData.website && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Website</Text>
              <Text size="sm" fw={500}>{businessData.website}</Text>
            </Group>
          )}
        </Stack>
      </Card>

      {/* Terms and Conditions */}
      <Card withBorder p="md" radius="md">
        <Stack gap="sm">
          <Checkbox
            label={
              <Text size="sm">
                I agree to the{' '}
                <Anchor href="/terms" target="_blank">
                  Terms and Conditions
                </Anchor>
                {' '}and{' '}
                <Anchor href="/privacy" target="_blank">
                  Privacy Policy
                </Anchor>
              </Text>
            }
            checked={termsAccepted}
            onChange={(e) => {
              setTermsAccepted(e.currentTarget.checked);
              setTermsError('');
            }}
            error={termsError}
          />
        </Stack>
      </Card>

      {/* Info about what happens next */}
      <Alert
        icon={<IconCheck size={16} />}
        title="What happens next?"
        color="blue"
      >
        {isAuthenticated ? (
          <Stack gap="xs">
            <Text size="sm">
              After submitting, your vendor profile will be created in <strong>Draft</strong> status.
              You'll need to complete a few more steps:
            </Text>
            <Text size="sm" component="ul" style={{ margin: 0, paddingLeft: 20 }}>
              <li>Upload required business documents</li>
              <li>Select your service locations</li>
              <li>Add your products or services</li>
              <li>Submit for review when ready</li>
            </Text>
          </Stack>
        ) : (
          <Stack gap="xs">
            <Text size="sm">
              After submitting, your account and vendor profile will be created.
              You'll receive a confirmation email with login instructions.
            </Text>
            <Text size="sm">
              Once logged in, you can complete your profile by uploading documents,
              selecting locations, and adding your products or services.
            </Text>
          </Stack>
        )}
      </Alert>

      <Group justify="space-between" mt="md">
        <Button
          variant="default"
          onClick={onBack}
          size="md"
          radius="md"
          disabled={isSubmitting}
        >
          Back
        </Button>
        <Button
          onClick={handleSubmit}
          size="md"
          radius="md"
          variant="gradient"
          gradient={{ from: 'indigo', to: 'violet' }}
          loading={isSubmitting}
          leftSection={<IconCheck size={16} />}
        >
          Submit Registration
        </Button>
      </Group>
    </Stack>
  );
}
